import { Injectable, inject } from "@angular/core";
import { Observable, Subject } from "rxjs";

import { IChartCriteria } from "@tylertech/tyler-api-types";

import { ChartRegistryService } from "./chartRegistryService.g";

@Injectable({ providedIn: "root" })
export class ChartManagerService
{
    private readonly $chartRegistryService = inject(ChartRegistryService);

    private readonly refreshChartSubject = new Subject<string>();
    private readonly chartsLoadedSubject = new Subject<void>();
    private readonly leftChartsSubject = new Subject<ReadonlyArray<IChartCriteria>>();
    private readonly rightChartsSubject = new Subject<ReadonlyArray<IChartCriteria>>();
    private readonly hiddenChartsSubject = new Subject<ReadonlyArray<IChartCriteria>>();

    get refreshChartObservable(): Observable<string> { return this.refreshChartSubject.asObservable(); }
    get chartsLoadedObservable(): Observable<void> { return this.chartsLoadedSubject.asObservable(); }

    get leftChartsObservable(): Observable<ReadonlyArray<IChartCriteria>> { return this.leftChartsSubject.asObservable(); }
    get rightChartsObservable(): Observable<ReadonlyArray<IChartCriteria>> { return this.rightChartsSubject.asObservable(); }
    get hiddenChartsObservable(): Observable<ReadonlyArray<IChartCriteria>> { return this.hiddenChartsSubject.asObservable(); }

    updateCharts(): Promise<void>
    {
        return this.fetchCharts(false);
    }

    private fetchCharts(reloadChartData: boolean): Promise<void>
    {
        return this.$chartRegistryService.GetChartCriterias()
            .then((criteriaList: Array<IChartCriteria>) =>
            {
                if (reloadChartData)
                {
                    criteriaList.forEach((criteria) =>
                    {
                        this.fireRefreshChart(criteria.ChartId);
                    });
                }
                else
                {
                    this.spiltToColumns(criteriaList);
                }

                this.fireAfterChartsLoaded();
            });
    }

    protected spiltToColumns(charts: ReadonlyArray<IChartCriteria>): void
    {
        const left: Array<IChartCriteria> = [];
        const right: Array<IChartCriteria> = [];
        const hidden: Array<IChartCriteria> = [];

        charts.forEach((crit) =>
        {
            const layoutParameter = crit.Parameters.find(v => v.Id.substring(v.Id.length - 6) === "Layout");
            if (getColumn(crit) === "X")
                hidden.push(crit);
            else if (layoutParameter?.Value === "0" || getColumn(crit) === "A")
                left.push(crit);
            else
                right.push(crit);
        });

        this.leftChartsSubject.next(left.sort(compareCharts));
        this.rightChartsSubject.next(right.sort(compareCharts));
        this.hiddenChartsSubject.next(hidden.sort(compareCharts));

        function compareCharts(a: IChartCriteria, b: IChartCriteria): number
        {
            const aPos = getRank(a);
            const bPos = getRank(b);
            if (aPos === bPos)
            {
                return a.ChartId.localeCompare(b.ChartId);
            }
            return aPos.localeCompare(bPos);
        }

        function getColumn(criteria: IChartCriteria): string
        {
            const position = criteria.Parameters.find(v => v.Caption === "Position");
            if (!position)
            {
                return "";
            }

            // TODO - I have no idea what this is testing for....
            const matches = /^([A-Z]*)-?.+?$/i.exec(position.Value);
            if (!matches)
            {
                return "";
            }
            return matches[1];
        }

        function getRank(criteria: IChartCriteria): string
        {
            const position = criteria.Parameters.find(v => v.Caption === "Position");
            if (!position)
            {
                return "";
            }

            // TODO - I have no idea what this is testing for....
            const matches = /^[A-Z]*-?(.+?)$/i.exec(position.Value);
            if (!matches)
            {
                return "";
            }
            return matches[1];
        }
    }

    private fireAfterChartsLoaded(): void
    {
        this.chartsLoadedSubject.next();
    }

    private fireRefreshChart(chartId: string): void
    {
        this.refreshChartSubject.next(chartId);
    }

    fireRefreshChartLayout(): Promise<void>
    {
        return this.fetchCharts(true);
    }
}
